import { useState, useEffect } from "react";
import { useNavigate } from "react-router";
import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/components/atomic-crm/providers/supabase/supabase";
import type { Scorecard } from "@/components/atomic-crm/types";

export function ScorecardList() {
    const [scorecards, setScorecards] = useState<Scorecard[]>([]);
    const [loading, setLoading] = useState(true);
    const navigate = useNavigate();

    useEffect(() => {
        supabase
            .from("scorecards")
            .select("*")
            .order("created_at", { ascending: false })
            .then(({ data }) => {
                if (data) setScorecards(data);
                setLoading(false);
            });
    }, []);

    const handleActivate = async (id: number) => {
        await supabase.from("scorecards").update({ is_active: false }).neq("id", id);
        await supabase.from("scorecards").update({ is_active: true }).eq("id", id);
        setScorecards((prev) => prev.map((s) => ({ ...s, is_active: s.id === id })));
    };

    return (
        <div className="mx-auto max-w-4xl p-6">
            <div className="mb-4 flex items-center justify-between">
                <h1 className="text-2xl font-bold">Scorecards</h1>
                <Button onClick={() => navigate("/scorecard/new")}>New Scorecard</Button>
            </div>
            {loading ? (
                <p className="text-muted-foreground text-sm">Loading…</p>
            ) : scorecards.length === 0 ? (
                <p className="text-muted-foreground text-sm">
                    No scorecards yet. Click "New Scorecard" to create one.
                </p>
            ) : (
                <div className="rounded-md border">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>Name</TableHead>
                                <TableHead>Status</TableHead>
                                <TableHead>Created</TableHead>
                                <TableHead className="text-right">Actions</TableHead>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {scorecards.map((sc) => (
                                <TableRow key={sc.id}>
                                    <TableCell className="font-medium">{sc.name}</TableCell>
                                    <TableCell>
                                        {sc.is_active ? (
                                            <Badge className="bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200">
                                                Active
                                            </Badge>
                                        ) : (
                                            <Badge variant="outline">Inactive</Badge>
                                        )}
                                    </TableCell>
                                    <TableCell className="text-muted-foreground">
                                        {new Date(sc.created_at).toLocaleDateString()}
                                    </TableCell>
                                    <TableCell className="text-right space-x-1">
                                        <Button
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => navigate(`/scorecard/${sc.id}`)}
                                        >
                                            Edit
                                        </Button>
                                        {!sc.is_active && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => handleActivate(sc.id)}
                                            >
                                                Activate
                                            </Button>
                                        )}
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}
        </div>
    );
}
